import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import {
  User,
  useUpdateMentorSlots,
  useMentorSlots,
} from "@/lib/api/user";
import { useAbility } from "@/lib/casl/ability-context";
import { hasPrivilegedAccessFromAbility } from "@/lib/casl/privileged-access";
import { isMenteeIdentity } from "@/lib/utils/system-roles";
import { UserDetailsCard } from "./user-details-card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { useEffect } from "react";

const mentorSlotsSchema = z.object({
  max_total_slots: z.number().min(0).max(100),
  max_daily_slots: z.number().min(0).max(10),
});

type MentorSlotsValues = z.infer<typeof mentorSlotsSchema>;

interface UserMentorSlotsProps {
  user: User;
}

export function UserMentorSlots({ user }: UserMentorSlotsProps) {
  const ability = useAbility();
  const hasPrivilegedAccess = hasPrivilegedAccessFromAbility(ability);
  const canUpdateMentorSlots =
    hasPrivilegedAccess && ability.can("update", "mentor-slots");
  const { data: slotConfig, isLoading: isLoadingSlots } = useMentorSlots(
    user.id,
  );
  const { mutate: updateSlots, isPending: isUpdating } = useUpdateMentorSlots();
  const { toast } = useToast();

  const form = useForm<MentorSlotsValues>({
    resolver: zodResolver(mentorSlotsSchema),
    defaultValues: {
      max_total_slots: 20,
      max_daily_slots: 2,
    },
  });

  useEffect(() => {
    if (slotConfig) {
      form.reset({
        max_total_slots: slotConfig.max_total_slots,
        max_daily_slots: slotConfig.max_daily_slots,
      });
    }
  }, [slotConfig, form]);

  const onSubmit = (values: MentorSlotsValues) => {
    updateSlots(
      {
        userId: user.id,
        data: values,
      },
      {
        onSuccess: () => {
          toast({
            title: "Success",
            description: "Mentor slots updated successfully",
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update mentor slots",
            variant: "destructive",
          });
        },
      },
    );
  };

  if (isMenteeIdentity(user.systemRoles)) return null;

  return (
    <UserDetailsCard>
      <h3 className="mb-4 text-lg font-semibold">Mentor Slots Configuration</h3>
      {isLoadingSlots ? (
        <div className="space-y-4">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="max_total_slots"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Total Slots</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={100}
                      {...field}
                      disabled={!canUpdateMentorSlots}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <p className="text-sm text-gray-500">
                    Maximum number of teens this mentor can have in total
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="max_daily_slots"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Daily New Teens</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={0}
                      max={10}
                      {...field}
                      disabled={!canUpdateMentorSlots}
                      onChange={(e) => field.onChange(Number(e.target.value))}
                    />
                  </FormControl>
                  <p className="text-sm text-gray-500">
                    Maximum number of new teens that can be assigned per day
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="submit"
              disabled={isUpdating || !canUpdateMentorSlots}
              className="w-full"
            >
              {isUpdating ? "Updating..." : "Update Slots"}
            </Button>
          </form>
        </Form>
      )}
    </UserDetailsCard>
  );
}
